import { V4 } from '@moleculeone/utils';
import { NORMAL, PART, elementHeights, elementWidths } from './elementSizes';

const circleRadius = 25;

function setLabelPositions(label) {
  let pivotShift;
  let labelWidth = 0;
  for (const part of label.parts) {
    setLabelPartPosition(part, labelWidth);
    if (part.pivot) {
      pivotShift = part.shift;
    }
    labelWidth += part.size[0];
  }
  for (const part of label.parts) {
    part.shift = V4.sub(part.shift, pivotShift);
  }

  label.bbox = {
    max: V4.neginfv,
    min: V4.posinfv,
  };

  for (const part of label.parts) {
    label.bbox.max = V4.max(label.bbox.max, V4.move(part.shift, part.size, 0.5));
    label.bbox.min = V4.min(label.bbox.min, V4.move(part.shift, part.size, -0.5));
  }
  label.bbox.size = V4.sub(label.bbox.max, label.bbox.min);
}

function setLabelPartPosition(part, currentWidth) {
  const textSize = part.type ? PART : NORMAL;

  const width = elementWidths[textSize][part.text] ?? 25;
  const height = elementHeights[textSize];
  part.size = V4.vect(width, height);

  const xShift = currentWidth + part.size[0] * 0.5;
  let yShift = 0;
  if (part.type === 'sub') {
    yShift = height / 2;
  }
  if (part.type === 'super') {
    yShift = -height / 2;
  }
  part.shift = V4.vect(xShift, yShift);
}

function setSideLabelDirection(drawnAtoms, drawnAtom) {
  const upright = V4.sub(drawnAtom.centerLabel.bbox.max, drawnAtom.sideLabel.bbox.min);
  const downleft = V4.sub(drawnAtom.centerLabel.bbox.min, drawnAtom.sideLabel.bbox.max);
  const shifts = [
    V4.vect(upright[0], 0),
    V4.vect(0, upright[1] + 5),
    V4.vect(downleft[0], 0),
    V4.vect(0, downleft[1] - 5),
  ];
  const keyedShifts = [];

  for (const shift of shifts) {
    const labelPos = V4.add(drawnAtom.pos, shift);
    const dist = Object.values(drawnAtoms)
      .filter(otheratom => drawnAtom.idx !== otheratom.idx)
      .map(otheratom => V4.dist(otheratom.pos, labelPos))
      .reduce((acc, r) => (r < acc ? r : acc), Number.POSITIVE_INFINITY);
    keyedShifts.push([shift, dist]);
  }
  // reverse order - smallest first
  keyedShifts.sort((v1, v2) => v2[1] - v1[1]);
  return keyedShifts[0][0];
}

function setLabelBBoxes(mol, drawnMol) {
  for (const [, drawnAtom] of Object.entries(drawnMol.atoms)) {
    drawnAtom.bbox = null;
    if (drawnAtom.centerLabel) {
      drawnAtom.bbox = { ...drawnAtom.centerLabel.bbox };
    }
    if (drawnAtom.sideLabel) {
      const side = drawnAtom.sideLabel;
      drawnAtom.bbox.max = V4.max(drawnAtom.bbox.max, V4.add(side.bbox.max, side.shift));
      drawnAtom.bbox.min = V4.min(drawnAtom.bbox.min, V4.add(side.bbox.min, side.shift));
    }
    if (drawnAtom.bbox == null) {
      drawnAtom.bbox = {
        max: V4.vect(circleRadius, circleRadius),
        min: V4.vect(-circleRadius, -circleRadius),
      };
    }
    drawnAtom.bbox.size = V4.sub(drawnAtom.bbox.max, drawnAtom.bbox.min);
  }
}

function setMolBBox(mol, drawnMol) {
  let bbox = {
    max: V4.neginfv,
    min: V4.posinfv,
  };
  for (const atom of Object.values(drawnMol.atoms)) {
    bbox.max = V4.max(bbox.max, V4.add(atom.bbox.max, atom.pos));
    bbox.min = V4.min(bbox.min, V4.add(atom.bbox.min, atom.pos));
  }
  if (bbox.max === V4.neginfv) {
    bbox = { max: V4.vect(100, 100), min: V4.vect(0, 0) };
  }
  bbox.size = V4.sub(bbox.max, bbox.min);

  drawnMol.bbox = bbox;
}

export function setLabelsLayout(mol, drawnMol) {
  for (const [, drawnAtom] of Object.entries(drawnMol.atoms)) {
    if (drawnAtom.centerLabel) {
      setLabelPositions(drawnAtom.centerLabel);
    }
    if (drawnAtom.sideLabel) {
      setLabelPositions(drawnAtom.sideLabel);
    }
  }
  for (const [, drawnAtom] of Object.entries(drawnMol.atoms)) {
    if (drawnAtom.centerLabel && drawnAtom.sideLabel) {
      drawnAtom.sideLabel.shift = setSideLabelDirection(drawnMol.atoms, drawnAtom);
    }
  }
  setLabelBBoxes(mol, drawnMol);
  setMolBBox(mol, drawnMol);
}
